import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Alert,
  Divider,
  useTheme,
  Fade,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import {
  PersonAdd as RegisterIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  AccountBalanceWallet as CurrencyIcon,
} from "@mui/icons-material";
import BrandIcon from "../../components/common/BrandIcon";
import { useRegister } from "../../hooks/auth";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH, VERIFY_EMAIL_ROUTE_PATH, VERIFY_EMAIL_CONFIRM_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { currencyOptions, popularCurrencyCodes } from "../../utils/currencies";
import PasswordStrengthMeter from "../../components/common/PasswordStrengthMeter";
import { isPasswordCompliant } from "../../utils/passwordPolicy";
import { validateInternationalName } from "../../utils/nameValidation";
import { ModernTextField, GradientButton } from "../../components/common/StyledComponents";

const REGISTER_SUCCESS_REDIRECT_DELAY_MS = 1500;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [currency, setCurrency] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { mutate: register, isPending: isLoading } = useRegister();

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    repeatPassword: "",
    currency: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
    repeatPassword: false,
    currency: false,
  });

  type Field = keyof typeof errors;

  const values: Record<Field, string> = {
    name,
    email,
    password,
    repeatPassword,
    currency,
  };

  const validateEmail = (value: string): string =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ? ""
      : "Please enter a valid email address like name@example.com.";

  const validatePassword = (value: string): string =>
    isPasswordCompliant(value) ? "" : "Use at least 8 chars with uppercase, lowercase, number, and symbol.";

  const validateName = (value: string): string => validateInternationalName(value);

  const validateRepeatPassword = (value: string, original: string): string =>
    value === original ? "" : "Passwords don't match. Please try again.";

  const validateCurrency = (value: string): string =>
    value ? "" : "Please select your preferred currency.";

  const setters: Record<Field, (v: string) => void> = {
    name: setName,
    email: setEmail,
    password: (v) => {
      setPassword(v);
      if (touched.repeatPassword) {
        setErrors((prev) => ({
          ...prev,
          repeatPassword: validateRepeatPassword(repeatPassword, v),
        }));
      }
    },
    repeatPassword: setRepeatPassword,
    currency: setCurrency,
  };

  const validators = (pwd: string): Record<Field, (v: string) => string> => ({
    name: validateName,
    email: validateEmail,
    password: validatePassword,
    repeatPassword: (v: string) => validateRepeatPassword(v, pwd),
    currency: validateCurrency,
  });

  const handleBlur = (field: Field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const v = values[field];
    setErrors((prev) => ({
      ...prev,
      [field]: validators(password)[field](v),
    }));
  };

  const handleChange = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setters[field](v);

    if (touched[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: validators(field === "password" ? v : password)[field](v),
      }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    setTouched({
      name: true,
      email: true,
      password: true,
      repeatPassword: true,
      currency: true,
    });

    const newErrors = {
      name: validateName(name),
      email: validateEmail(email),
      password: validatePassword(password),
      repeatPassword: validateRepeatPassword(repeatPassword, password),
      currency: validateCurrency(currency),
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((e) => e !== "");
    if (hasErrors) {
      return;
    }

    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${VERIFY_EMAIL_CONFIRM_ROUTE_PATH}`;
    const verificationCallbackUrl = new URL(path, window.location.origin).toString();

    register(
      { email, password, name, currency, verificationCallbackUrl },
      {
        onSuccess: () => {
          setSuccessMessage("Account created successfully! We've sent a verification email.");
          // Short delay to show success message before redirect to verify page
          setTimeout(() => {
            navigate(VERIFY_EMAIL_ROUTE_PATH, { state: { email } });
          }, REGISTER_SUCCESS_REDIRECT_DELAY_MS);
        },
        onError: (error) => {
          console.error("Registration failed:", error);
          if (error instanceof ApiError) {
            setSubmitError(error.message);
          } else {
            setSubmitError("Registration failed. Please try again.");
          }
        }
      }
    );
  };

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleClickShowRepeatPassword = () => {
    setShowRepeatPassword((show) => !show);
  };

  // Group currencies: popular first, then all others alphabetically
  const popularCurrencies = currencyOptions.filter(c => popularCurrencyCodes.includes(c.code));
  const otherCurrencies = currencyOptions
    .filter(c => !popularCurrencyCodes.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const groupedCurrencies = [
    ...popularCurrencies,
    ...otherCurrencies,
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Fade in={true} timeout={800}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 5 }}>
              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <BrandIcon size={64} />
                </Box>
                
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Create Account
                </Typography>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  Join PocketBalance and take control of your finances
                </Typography>
              </Box>

              {/* Success Message */}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  {successMessage}
                </Alert>
              )}

              {/* Error Alert */}
              {submitError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {submitError}
                </Alert>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  {/* Name Field */}
                  <Grid item xs={12} sm={6}>
                    <ModernTextField
                      fullWidth
                      id="name"
                      label="Full Name"
                      name="name"
                      autoComplete="name"
                      autoFocus
                      value={name}
                      onChange={handleChange("name")}
                      onBlur={() => handleBlur("name")}
                      error={!!errors.name && touched.name}
                      helperText={touched.name ? errors.name : `${name.length}/100 characters`}
                      required
                      disabled={isLoading}
                      inputProps={{ maxLength: 100 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Email Field */}
                  <Grid item xs={12} sm={6}>
                    <ModernTextField
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={handleChange("email")}
                      onBlur={() => handleBlur("email")}
                      error={!!errors.email && touched.email}
                      helperText={touched.email ? errors.email : "We'll never share your email"}
                      required
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Password Field */}
                  <Grid item xs={12} sm={6}>
                    <ModernTextField
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={handleChange("password")}
                      onBlur={() => handleBlur("password")}
                      error={!!errors.password && touched.password}
                      helperText={touched.password ? errors.password : "Must include upper letters, lower letters, number, and symbol"}
                      required
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              disabled={isLoading}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <PasswordStrengthMeter password={password} />
                  </Grid>

                  {/* Repeat Password Field */}
                  <Grid item xs={12} sm={6}>
                    <ModernTextField
                      fullWidth
                      name="repeatPassword"
                      label="Confirm Password"
                      type={showRepeatPassword ? 'text' : 'password'}
                      id="repeatPassword"
                      autoComplete="new-password"
                      value={repeatPassword}
                      onChange={handleChange("repeatPassword")}
                      onBlur={() => handleBlur("repeatPassword")}
                      error={!!errors.repeatPassword && touched.repeatPassword}
                      helperText={touched.repeatPassword ? errors.repeatPassword : "Must match your password"}
                      required
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowRepeatPassword}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              disabled={isLoading}
                            >
                              {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                                    {/* Currency Field */}
                  <Grid item xs={12}>
                    <Autocomplete
                      fullWidth
                      value={currencyOptions.find(c => c.code === currency) || null}
                      onChange={(_, newValue) => {
                        const newCurrency = newValue?.code || "";
                        setCurrency(newCurrency);
                        if (touched.currency) {
                          setErrors((prev) => ({
                            ...prev,
                            currency: validateCurrency(newCurrency),
                          }));
                        }
                      }}
                      onBlur={() => handleBlur("currency")}
                      options={groupedCurrencies}
                      groupBy={(option) => 
                        popularCurrencyCodes.includes(option.code) ? "Popular Currencies" : "All Currencies"
                      }
                      getOptionLabel={(option) => `${option.name} (${option.code})`}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, minWidth: 30 }}>
                            {option.symbol}
                          </Typography>
                          <Typography>
                            {option.name}
                          </Typography>
                          <Typography color="text.secondary">
                            ({option.code})
                          </Typography>
                        </Box>
                      )}
                      disabled={isLoading}
                      renderInput={(params) => (
                        <ModernTextField
                          {...params}
                          label="Preferred Currency"
                          error={!!errors.currency && touched.currency}
                          helperText={touched.currency ? errors.currency : "Search or select your default currency"}
                          required
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <CurrencyIcon color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <GradientButton
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RegisterIcon />}
                  sx={{
                    py: 1.5,
                    mt: 4,
                    mb: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 8px 25px ${theme.palette.primary.main}30`,
                    },
                    '&:disabled': {
                      background: theme.palette.action.disabledBackground,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </GradientButton>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: "center" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigate(LOGIN_ROUTE_PATH)}
                    sx={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: '1rem',
                      fontFamily: theme.typography.fontFamily,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Sign in to your account →
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default RegisterPage;
